import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink, faPlus } from "@fortawesome/free-solid-svg-icons";
import LinkEditModal from "./LinkEditModal";

// Shared pill geometry for every CTA-row button (the custom CTA and the waitlist
// CTA sit side by side, so they have to be dimensionally identical). Exported
// with CTA_COLOR_PRESETS so WaitlistCtaButton reuses both instead of copying them.
export const CTA_PILL_BASE =
  "tw-inline-flex tw-items-center tw-justify-center tw-min-w-[25vw] md:tw-min-w-[15vw] tw-max-w-[280px] sm:tw-max-w-[320px] md:tw-max-w-none tw-truncate tw-h-12 min-[375px]:tw-h-14 sm:tw-h-16 tw-px-6 sm:tw-px-6 md:tw-px-8 tw-rounded-full tw-font-bold tw-text-sm md:tw-text-base tw-transition-colors";

// Preset keys must stay in sync with CTA_COLOR_KEYS in server/models/fepk.js
// (the single source of truth, also imported by server/middleware/validator.js).
export const CTA_COLOR_PRESETS = {
  pink: {
    label: "Pink",
    swatch: "tw-bg-[#FF43A7]",
    button: "tw-bg-[#FF43A7] tw-text-[#570033] hover:tw-bg-[#ff5cac] tw-shadow-[0_10px_25px_rgba(255,67,167,0.35)]",
  },
  gold: {
    label: "Gold",
    swatch: "tw-bg-[#FFC145]",
    button: "tw-bg-[#FFC145] tw-text-[#5C3A00] hover:tw-bg-[#ffcf6b] tw-shadow-[0_10px_25px_rgba(255,193,69,0.35)]",
  },
  teal: {
    label: "Teal",
    swatch: "tw-bg-[#2DD4BF]",
    button: "tw-bg-[#2DD4BF] tw-text-[#003E38] hover:tw-bg-[#5be3d2] tw-shadow-[0_10px_25px_rgba(45,212,191,0.35)]",
  },
  blue: {
    label: "Sky Blue",
    swatch: "tw-bg-[#38BDF8]",
    button: "tw-bg-[#38BDF8] tw-text-[#012A3A] hover:tw-bg-[#67cdfa] tw-shadow-[0_10px_25px_rgba(56,189,248,0.35)]",
  },
  green: {
    label: "Emerald",
    swatch: "tw-bg-[#4ADE80]",
    button: "tw-bg-[#4ADE80] tw-text-[#063214] hover:tw-bg-[#75e6a1] tw-shadow-[0_10px_25px_rgba(74,222,128,0.35)]",
  },
  violet: {
    label: "Lavender",
    swatch: "tw-bg-[#A78BFA]",
    button: "tw-bg-[#A78BFA] tw-text-[#2E1065] hover:tw-bg-[#bda6fb] tw-shadow-[0_10px_25px_rgba(167,139,250,0.35)]",
  },
};
const DEFAULT_CTA_COLOR = "pink";

const isValidCtaUrl = (value) => {
  if (!value) return false;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "https:") return false;
    // Mirror the server's require_tld rule (server/middleware/validator.js) so a
    // URL accepted here isn't later rejected by the backend (e.g. https://localhost).
    return parsed.hostname.includes(".") && !parsed.hostname.endsWith(".");
  } catch {
    return false;
  }
};

export default function EpkCtaButton({ epkInfo, isEditMode, onChange }) {
  const { t } = useTranslation();
  const currentLabel = epkInfo?.custom_cta?.label || "";
  const currentUrl = epkInfo?.custom_cta?.url || "";
  const currentColor = CTA_COLOR_PRESETS[epkInfo?.custom_cta?.color]
    ? epkInfo.custom_cta.color
    : DEFAULT_CTA_COLOR;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempLabel, setTempLabel] = useState(currentLabel);
  const [tempUrl, setTempUrl] = useState(currentUrl);
  const [tempColor, setTempColor] = useState(currentColor);

  useEffect(() => {
    setTempLabel(currentLabel);
    setTempUrl(currentUrl);
    setTempColor(currentColor);
  }, [currentLabel, currentUrl, currentColor]);

  const isUrlValid = tempUrl === "" || isValidCtaUrl(tempUrl);
  const canSave = tempLabel.trim() !== "" && isValidCtaUrl(tempUrl);

  const handleSave = () => {
    if (!canSave) return;
    onChange("custom_cta", { label: tempLabel.trim(), url: tempUrl.trim(), color: tempColor });
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setTempLabel(currentLabel);
    setTempUrl(currentUrl);
    setTempColor(currentColor);
    setIsModalOpen(false);
  };

  // Server-side validation already restricts custom_cta.url to https on save, but an
  // <a href> is a sensitive-enough sink (a stored javascript: URI executes on click)
  // to also gate rendering here, e.g. against data saved before that validation existed.
  const isCurrentUrlSafe = isValidCtaUrl(currentUrl);

  if (!isEditMode && !currentUrl) return null;

  return (
    <div className="tw-flex tw-items-center tw-shrink-0">
      {currentUrl && isCurrentUrlSafe ? (
        <a
          href={currentUrl}
          target="_blank"
          rel="noopener noreferrer nofollow ugc"
          onClick={(e) => {
            if (isEditMode) {
              e.preventDefault();
              setIsModalOpen(true);
            }
          }}
          className={`${CTA_PILL_BASE} tw-cursor-pointer ${CTA_COLOR_PRESETS[currentColor].button}`}
        >
          {currentLabel}
        </a>
      ) : (
        isEditMode && (
          <button
            onClick={() => setIsModalOpen(true)}
            className={`${CTA_PILL_BASE} tw-gap-2 tw-bg-transparent tw-border-[1.5px] tw-border-dashed tw-border-[#FF43A7] tw-text-[#FF43A7] hover:tw-bg-[#FF43A7]/10 tw-cursor-pointer`}
          >
            <FontAwesomeIcon icon={faPlus} />
            {t("Add Custom Button")}
          </button>
        )
      )}

      {isModalOpen && (
        <LinkEditModal
          title={t("Add Custom Button")}
          onCancel={handleCancel}
          onSave={handleSave}
          saveDisabled={!canSave}
        >
          <div className="tw-flex tw-flex-col tw-gap-2">
            <label className="tw-text-[#F0DBFF]/70 tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest">
              {t("Button Label")}
            </label>
            <input
              type="text"
              value={tempLabel}
              onChange={(e) => setTempLabel(e.target.value)}
              maxLength={40}
              placeholder={t("Enter a label (e.g. Buy Tickets)")}
              className="tw-w-full tw-bg-[#280D41] tw-border-2 tw-border-dashed tw-border-[#FF43A7]/40 tw-rounded-lg tw-py-[16px] tw-px-4 tw-text-[16px] tw-text-white tw-outline-none placeholder:tw-text-[#F0DBFF]/30 tw-transition-colors focus:tw-border-[#FF43A7]"
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-2">
            <label className="tw-text-[#F0DBFF]/70 tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest">
              {t("Button URL")}
            </label>
            <div className="tw-relative tw-w-full">
              <div className="tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2">
                <FontAwesomeIcon icon={faLink} className="tw-text-[#AA8894]" />
              </div>
              <input
                type="url"
                value={tempUrl}
                onChange={(e) => setTempUrl(e.target.value)}
                placeholder={t("Enter a secure URL (https://...)")}
                className={`tw-w-full tw-bg-[#280D41] tw-border-2 tw-border-dashed tw-rounded-lg tw-py-[16px] tw-pl-[48px] tw-pr-4 tw-text-[16px] tw-text-white tw-outline-none placeholder:tw-text-[#F0DBFF]/30 tw-transition-colors focus:tw-border-[#FF43A7] ${
                  isUrlValid ? "tw-border-[#FF43A7]/40" : "tw-border-red-500"
                }`}
              />
            </div>
            {!isUrlValid && (
              <span className="tw-text-red-400 tw-text-xs">
                {t("Please enter a valid https URL")}
              </span>
            )}
          </div>

          <div className="tw-flex tw-flex-col tw-gap-2">
            <label className="tw-text-[#F0DBFF]/70 tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest">
              {t("Button Color")}
            </label>
            <div className="tw-flex tw-gap-3">
              {Object.entries(CTA_COLOR_PRESETS).map(([key, preset]) => {
                const isSelected = tempColor === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTempColor(key)}
                    aria-label={t(preset.label)}
                    aria-pressed={isSelected}
                    className={`tw-w-8 tw-h-8 tw-rounded-full tw-transition-transform tw-cursor-pointer ${preset.swatch} ${
                      isSelected
                        ? "tw-ring-2 tw-ring-offset-2 tw-ring-offset-[#1F0439] tw-ring-white tw-scale-110"
                        : "hover:tw-scale-105"
                    }`}
                  />
                );
              })}
            </div>
          </div>
        </LinkEditModal>
      )}
    </div>
  );
}
